/**
 * Arrastar cartões do kanban com Pointer Events — o mesmo padrão do Painel
 * da Produção v2 (HTML5 drag-and-drop não funciona no toque do tablet).
 *
 * - Mouse: arrasta depois de 5 px.
 * - Toque: gesto horizontal depois de 10 px arrasta; gesto vertical é
 *   rolagem da coluna; segurar parado por 350 ms também começa o arraste
 *   (é o jeito de mover para outro posto da MESMA coluna, que fica abaixo).
 * - Soltar fora de uma área, ou na de origem, cancela sem ação.
 *
 * O estado por movimento fica em ref (nada re-renderiza por frame);
 * `arraste` só muda quando algo visível muda (começou, trocou de área).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const LIMIAR_MOUSE = 5;
const LIMIAR_TOQUE = 10;
const TOQUE_LONGO_MS = 350;

function areaEmPonto(areas, x, y) {
  for (const [id, el] of areas) {
    const r = el.getBoundingClientRect();
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
  }
  return null;
}

// Enquanto arrasta no toque, a página não pode rolar por baixo do dedo.
function impedirRolagem(evento) {
  if (evento.cancelable) evento.preventDefault();
}

/**
 * useArrastarCartoes({habilitado, onSoltar(item, idDaArea)}) →
 *   {arraste, registrarArea(id) → ref, propsDoCartao(item, idDeOrigem, chave),
 *    registrarFantasma}
 */
export default function useArrastarCartoes({ habilitado, onSoltar }) {
  const areasRef = useRef(new Map());
  const infoRef = useRef(null);
  const fantasmaRef = useRef(null);
  const posicaoRef = useRef({ x: 0, y: 0 });
  const suprimirCliqueRef = useRef(false);
  const onSoltarRef = useRef(onSoltar);
  const [arraste, setArraste] = useState(null); // {chave, origem, sobre, largura}

  useEffect(() => {
    onSoltarRef.current = onSoltar;
  }, [onSoltar]);

  const pararRolagemBloqueada = useCallback(() => {
    document.removeEventListener("touchmove", impedirRolagem);
  }, []);

  const encerrar = useCallback(() => {
    const info = infoRef.current;
    if (info?.timer) clearTimeout(info.timer);
    infoRef.current = null;
    pararRolagemBloqueada();
    setArraste(null);
  }, [pararRolagemBloqueada]);

  useEffect(() => encerrar, [encerrar]);

  const moverFantasma = useCallback((x, y) => {
    posicaoRef.current = { x, y };
    if (fantasmaRef.current) fantasmaRef.current.style.transform = `translate(${x}px, ${y}px) rotate(1.5deg)`;
  }, []);

  // O fantasma monta depois que o arraste começa: aplica a última posição.
  const registrarFantasma = useCallback((el) => {
    fantasmaRef.current = el;
    if (el) {
      const { x, y } = posicaoRef.current;
      el.style.transform = `translate(${x}px, ${y}px) rotate(1.5deg)`;
    }
  }, []);

  const registrarArea = useCallback(
    (id) => (el) => {
      if (el) areasRef.current.set(id, el);
      else areasRef.current.delete(id);
    },
    []
  );

  const ativar = useCallback(
    (x, y) => {
      const info = infoRef.current;
      if (!info || info.ativo) return;
      info.ativo = true;
      if (info.timer) clearTimeout(info.timer);
      if (info.tipoPonteiro === "touch") {
        document.addEventListener("touchmove", impedirRolagem, { passive: false });
      }
      moverFantasma(x - info.deslocX, y - info.deslocY);
      setArraste({ chave: info.chave, item: info.item, origem: info.origem, sobre: null, largura: info.largura });
    },
    [moverFantasma]
  );

  const aoApertar = useCallback(
    (evento, item, origem, chave) => {
      if (!habilitado) return;
      if (evento.button !== undefined && evento.button !== 0) return;
      const r = evento.currentTarget.getBoundingClientRect();
      const tipoPonteiro = evento.pointerType || "mouse";
      const info = {
        item,
        origem,
        chave,
        inicioX: evento.clientX,
        inicioY: evento.clientY,
        ultimoX: evento.clientX,
        ultimoY: evento.clientY,
        deslocX: evento.clientX - r.left,
        deslocY: evento.clientY - r.top,
        largura: r.width,
        tipoPonteiro,
        ativo: false,
        sobre: null,
        timer: null,
      };
      if (tipoPonteiro === "touch") {
        info.timer = setTimeout(() => ativar(info.ultimoX, info.ultimoY), TOQUE_LONGO_MS);
      }
      infoRef.current = info;
      evento.currentTarget.setPointerCapture?.(evento.pointerId);
    },
    [habilitado, ativar]
  );

  const aoMover = useCallback(
    (evento) => {
      const info = infoRef.current;
      if (!info) return;
      info.ultimoX = evento.clientX;
      info.ultimoY = evento.clientY;
      const dx = evento.clientX - info.inicioX;
      const dy = evento.clientY - info.inicioY;
      if (!info.ativo) {
        const limiar = info.tipoPonteiro === "touch" ? LIMIAR_TOQUE : LIMIAR_MOUSE;
        if (Math.abs(dx) < limiar && Math.abs(dy) < limiar) return;
        if (info.tipoPonteiro === "touch" && Math.abs(dy) > Math.abs(dx)) {
          // Rolagem da coluna, não arraste.
          if (info.timer) clearTimeout(info.timer);
          infoRef.current = null;
          return;
        }
        ativar(evento.clientX, evento.clientY);
      }
      moverFantasma(evento.clientX - info.deslocX, evento.clientY - info.deslocY);
      const sobre = areaEmPonto(areasRef.current, evento.clientX, evento.clientY);
      if (sobre !== info.sobre) {
        info.sobre = sobre;
        setArraste((atual) => (atual ? { ...atual, sobre } : atual));
      }
    },
    [ativar, moverFantasma]
  );

  const aoSoltar = useCallback(() => {
    const info = infoRef.current;
    if (!info) return;
    if (info.timer) clearTimeout(info.timer);
    infoRef.current = null;
    pararRolagemBloqueada();
    if (!info.ativo) return; // toque seco: segue para o onClick do cartão
    suprimirCliqueRef.current = true;
    setArraste(null);
    if (info.sobre && info.sobre !== info.origem) onSoltarRef.current?.(info.item, info.sobre);
  }, [pararRolagemBloqueada]);

  // Depois de um arraste o navegador ainda dispara o click: não abre o detalhe.
  const aoClicarCaptura = useCallback((evento) => {
    if (suprimirCliqueRef.current) {
      suprimirCliqueRef.current = false;
      evento.preventDefault();
      evento.stopPropagation();
    }
  }, []);

  const propsDoCartao = useCallback(
    (item, origem, chave) => ({
      onPointerDown: (evento) => aoApertar(evento, item, origem, chave),
      onPointerMove: aoMover,
      onPointerUp: aoSoltar,
      onPointerCancel: encerrar,
      onClickCapture: aoClicarCaptura,
    }),
    [aoApertar, aoMover, aoSoltar, encerrar, aoClicarCaptura]
  );

  return { arraste, registrarArea, propsDoCartao, registrarFantasma };
}
